/**
 * How a service record's mileage is presented, in one place.
 *
 * A service record's mileage can come from three places, and one of them must
 * never be shown bare. `mileage_source = 'mot'` means the number is the
 * odometer reading from the vehicle's last MOT — which can be eleven months
 * old. It is far better than "Mileage not recorded" (the reading is real, and
 * it was in the database the whole time), but printed on a customer's service
 * history next to a date it would read as "this is what the clock said when we
 * did the work", and it is not.
 *
 * So a MOT-sourced figure always carries its qualifier, everywhere: the
 * customer's profile, the service-history modal and the PDF that gets emailed.
 */

export type ServiceMileageSource = 'job' | 'vehicle' | 'recorded' | 'mot' | null | undefined;

export interface FormattedServiceMileage {
    /** e.g. "41,381 mi" — never rendered without `qualifier` when set. */
    value: string;
    /** e.g. "at last MOT" or "at last MOT, 14 Feb 2026". Null when the
     *  reading was recorded on the work. */
    qualifier: string | null;
}

export function formatServiceMileage(
    mileage: number | string | null | undefined,
    source: ServiceMileageSource,
    unit: 'mi' | 'miles' = 'mi',
    motDate?: string | null,
): FormattedServiceMileage | null {
    if (mileage == null || mileage === '' || !Number.isFinite(Number(mileage))) return null;
    const when = motDate ? new Date(motDate) : null;
    const whenText = when && !Number.isNaN(when.getTime())
        ? when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
    return {
        value: `${Number(mileage).toLocaleString('en-GB')} ${unit}`,
        qualifier: source === 'mot' ? (whenText ? `at last MOT, ${whenText}` : 'at last MOT') : null,
    };
}

/** "122,133 mi" or "122,133 mi (at last MOT, 14 Feb 2026)" — one line of text. */
export function mileageLine(
    mileage: number | string | null | undefined,
    source: ServiceMileageSource,
    motDate?: string | null,
): string | null {
    const m = formatServiceMileage(mileage, source, 'mi', motDate);
    if (!m) return null;
    return m.qualifier ? `${m.value} (${m.qualifier})` : m.value;
}
